import React from "react";
import { Pencil, Trash, X, MoreVertical, Eye } from "lucide-react";
import { IconButton, Menu, MenuItem, Switch } from "@mui/material";

const BrandCard = ({
  brand,
  onView,
  onEdit,
  onDelete,
  onRestore,
  isDeleted,
  onStatusChange,
  isStatusUpdating,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 hover:border-[#5652B0] transition-colors overflow-hidden ${
        isDeleted ? "opacity-75" : ""
      }`}>
      <div className="p-4">
        {/* Brand Status & Type Badges */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full 
              ${
                brand.status === "ACTIVE"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}>
              {brand.status}
            </span>
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full 
              ${
                brand.brand_type === "DOMESTIC"
                  ? "bg-blue-50 text-blue-700"
                  : "bg-orange-50 text-orange-700"
              }`}>
              {brand.brand_type === "DOMESTIC" ? "Domestic" : "International"}
            </span>
          </div>
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVertical size={16} />
          </IconButton>
        </div>

        {/* Brand Image */}
        <div className="flex items-center justify-center mb-4">
          <div className="relative w-24 h-24">
            <img
              src={brand.image || require("../../../../../assets/brand.png")}
              alt={brand.name}
              className="w-full h-full rounded-full object-cover"
              onError={e => {
                e.target.onerror = null;
                e.target.src = require("../../../../../assets/brand.png");
              }}
            />
          </div>
        </div>

        {/* Brand Info */}
        <div className="text-center">
          <h3 className="font-medium text-gray-900">{brand.name}</h3>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className="text-sm text-gray-500">
              {brand.metadata?.products_count || 0} Products
            </span>
            {brand.orderValidation?.validationType === "MOQ_MOV" && (
              <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                Order Validation
              </span>
            )}
          </div>
          {/* {brand.description && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">
              {brand.description}
            </p>
          )} */}
        </div>

        {/* Order Validation Details */}
        {brand.orderValidation?.validationType === "MOQ_MOV" && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {brand.orderValidation.minOrderQty && (
                <div className="text-center">
                  <p className="text-gray-500">Min Order Qty</p>
                  <p className="font-medium text-gray-700">
                    {brand.orderValidation.minOrderQty}
                  </p>
                </div>
              )}
              {brand.orderValidation.minOrderValue && (
                <div className="text-center">
                  <p className="text-gray-500">Min Order Value</p>
                  <p className="font-medium text-gray-700">
                    ₹{brand.orderValidation.minOrderValue}
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-center text-gray-500 mt-1">
              Condition:{" "}
              {brand.orderValidation.validationCondition === "BOTH"
                ? "Both conditions required"
                : "Any one condition required"}
            </p>
          </div>
        )}

        {/* Status Toggle */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-center">
          <Switch
            checked={brand.status === "ACTIVE"}
            onChange={() => !isStatusUpdating && onStatusChange(brand)}
            disabled={isStatusUpdating}
            size="small"
            sx={{
              "&.Mui-checked": {
                color: "#5652B0",
              },
              "&.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#5652B0",
              },
            }}
          />
          <span className="ml-2 text-sm text-gray-600">
            {brand.status === "ACTIVE" ? "Active" : "Inactive"}
          </span>
        </div>

        {isDeleted ? (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">Deleted Brand</span>
            <button
              onClick={() => onRestore(brand)}
              className="text-sm text-green-600 hover:text-green-700 font-medium">
              Restore Brand
            </button>
          </div>
        ) : null}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            onView(brand);
            handleMenuClose();
          }}>
          <Eye size={16} className="mr-2" />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            onEdit(brand);
            handleMenuClose();
          }}>
          <Pencil size={16} className="mr-2" />
          Edit
        </MenuItem>
        {!isDeleted && (
          <MenuItem
            onClick={() => {
              onDelete(brand);
              handleMenuClose();
            }}
            className="text-red-600">
            <Trash size={16} className="mr-2" />
            Delete
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default BrandCard;
